import { PublicKey } from '@solana/web3.js';
import { usePrivyEmbeddedWallet } from './usePrivyEmbeddedWallet';
import { useSubAccountsDelegation } from './useSubAccountsDelegation';
import { useCallback, useMemo, useRef, useState } from 'react';
import { notify } from 'src/utils/notifications';
import { useCreateWallet } from '@privy-io/react-auth/solana';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { EnableSignlessTradingState } from 'src/components/Modals/Onboarding/EnableSignlessTradingModal/EnableSignlessTradingStepCard';
import { useAuthenticateSolanaEoaWallet } from './useAuthenticateSolanaEoaWallet';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { useWalletsV2 } from '../useWalletsV2';
import useCurrentSettings from 'src/hooks/useCurrentSettings';

/**
 * Sets up signless trading using a Privy embedded wallet as a delegate for all subaccounts.
 *
 * This is a two-step process:
 * 1. Creates a Privy account by authenticating with the EOA wallet. An embedded wallet will be created (if it doesn't exist) once account is created in `useAutoCreatePrivyEmbeddedWallet`
 * 2. Sets up the embedded wallet as a delegate for all subaccounts.
 *
 * User will only be prompted to enable signless trading for all allowable subaccounts if their current user account isn't delegated out.
 */
export const useSetupSignlessTrading = () => {
	const currentUserAccount = useCurrentUserAccount();
	const {
		embeddedWallet,
		eoaWallet,
		user: privyUser,
	} = usePrivyEmbeddedWallet();

	const {
		isCurrentUserAccountDelegatable,
		allUndelegatedAuthorityAccounts,
		setupDelegateForSubaccounts,
	} = useSubAccountsDelegation();

	const { handleAuthenticateSolanaEoaWallet } =
		useAuthenticateSolanaEoaWallet();
	const { createWallet } = useCreateWallet();
	const appEventEmitter = useAppEventEmitter();
	const { captureEvent } = usePostHogCapture();
	const isEmbeddedWalletFlagEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_EMBEDDED_WALLET')
	);
	const [currentSettings] = useCurrentSettings();
	const isEmbeddedWalletEnabled =
		isEmbeddedWalletFlagEnabled && currentSettings.enableSignlessTrading;
	const { authorityWallet } = useWalletsV2();

	const [connectingToPrivyState, setConnectingToPrivyState] =
		useState<EnableSignlessTradingState>('NOT_STARTED');
	const [settingUpDelegatesState, setSettingUpDelegatesState] =
		useState<EnableSignlessTradingState>('NOT_STARTED');

	const createWalletRef = useSyncedRef(createWallet); // there seems to a bug with createWallet constantly being re-created, causing the useEffect to run multiple times unnecessarily
	const isSettingUpDelegatesRef = useRef(false);

	const isCurrentAccountDelegatedToEmbeddedWallet = useMemo(() => {
		if (!embeddedWallet || !currentUserAccount) return false;

		return currentUserAccount.delegate.equals(
			new PublicKey(embeddedWallet.address)
		);
	}, [currentUserAccount, embeddedWallet]);

	const toPromptSignlessTradingSetup =
		isEmbeddedWalletEnabled &&
		!!eoaWallet &&
		(!embeddedWallet || isCurrentUserAccountDelegatable); // !embeddedWallet assumes that the user is not signed in to Privy. this doesn't account for cases where user is signed into Privy, but doesn't have an embedded wallet

	const connectToPrivy = useCallback(
		async (isLedger: boolean) => {
			if (!eoaWallet) return;

			if (privyUser) {
				setConnectingToPrivyState('SUCCESS');
				return;
			}

			setConnectingToPrivyState('AWAITING');

			const isAuthenticated = await handleAuthenticateSolanaEoaWallet(
				eoaWallet,
				isLedger
			);

			if (!isAuthenticated) {
				setConnectingToPrivyState('FAILED');
				notify({
					type: 'error',
					message: 'Failed to connect.',
				});
				return;
			}

			// attempt to create a new embedded wallet, will throw an error if the user already has an embedded wallet
			try {
				await createWalletRef.current({
					createAdditional: false,
				});
			} catch (error: unknown) {
				if (
					!(error as Error).message.includes(
						'User already has an embedded wallet.'
					)
				) {
					// an unexpected error, but we allow flow to continue
					console.error(error);
				}
			}

			setConnectingToPrivyState('SUCCESS');
		},
		[eoaWallet, !!privyUser, handleAuthenticateSolanaEoaWallet]
	);

	// The returned boolean indicates whether to hide the modal or not
	const setupDelegates = useCallback(
		async (isLedger: boolean) => {
			if (isSettingUpDelegatesRef.current) return false;

			if (!embeddedWallet?.address || !privyUser) return false;

			if (isCurrentAccountDelegatedToEmbeddedWallet) {
				setSettingUpDelegatesState('SUCCESS');
				return true;
			}

			if (allUndelegatedAuthorityAccounts.length === 0) {
				notify({
					type: 'info',
					message: 'No eligible subaccounts to enable Auto-Signing for.',
				});
				appEventEmitter.emit('showSettingsAutoSigningTooltip');
				return true;
			}

			if (!isCurrentUserAccountDelegatable) {
				notify({
					type: 'info',
					message: 'Your current account is not eligible for Auto-Signing.',
				});
				appEventEmitter.emit('showSettingsAutoSigningTooltip');
				return true;
			}

			isSettingUpDelegatesRef.current = true;

			setSettingUpDelegatesState('AWAITING');

			try {
				await setupDelegateForSubaccounts(
					new PublicKey(embeddedWallet.address),
					false,
					authorityWallet ?? undefined
				);

				setSettingUpDelegatesState('SUCCESS');
			} catch (error: unknown) {
				console.error(error);
				notify({
					type: 'error',
					message: 'Failed to enable Auto-Signing',
				});
				setSettingUpDelegatesState('FAILED');

				isSettingUpDelegatesRef.current = false;
				return true;
			}

			captureEvent('signless_trading_setup_success', {
				is_ledger: isLedger,
			});

			notify({
				type: 'success',
				message: 'Enabled Auto-Signing for trading.',
			});

			isSettingUpDelegatesRef.current = false;

			return true;
		},
		[
			embeddedWallet?.address,
			!!privyUser,
			isCurrentUserAccountDelegatable,
			isCurrentAccountDelegatedToEmbeddedWallet,
			allUndelegatedAuthorityAccounts.length,
			appEventEmitter,
			setupDelegateForSubaccounts,
			setSettingUpDelegatesState,
			captureEvent,
			authorityWallet,
		]
	);

	return {
		toPromptSignlessTradingSetup,
		connectingToPrivyState,
		settingUpDelegatesState,
		connectToPrivy,
		setupDelegates,
	};
};
